import EventEmitter from "event-lite";

import { PATH_DELIMITER } from "@/__main__/constants.mjs";
import { devDebug } from "@/util/dev.mjs";
import digPath from "@/util/dig-path.mjs";

export const MAX_LENGTH = 99;

type Config = {
  MAX_LENGTH: number;
  STATE: object | null;
  INITIAL_PATHS: Set<string>;
};

export const CONFIG: Config = {
  // The lower bound for the max amount of paths to keep is exactly how many
  // paths were written to from any route's fetchData method, plus any actions
  // that write paths.
  //
  // It is potentially unsafe to write too many different keys on the same
  // route, because then data can be cleaned up without calling fetchData again
  // to ensure that keys still exist.
  MAX_LENGTH,

  // This needs to be set externally.
  STATE: null,
  INITIAL_PATHS: new Set(),
};

export const emitter: EventEmitter = new EventEmitter();
export const EVENT_ADD_KEY = "EVENT_ADD_KEY";

export const keyPaths: Set<string> = new Set();

export default function writeKeyPath(
  keyPath: readonly PropertyKey[],
  isDelete = false,
) {
  const key: string = keyPath.join(PATH_DELIMITER);

  // Do not add key paths if they belong in the initial set.
  if (CONFIG.INITIAL_PATHS.has(key)) {
    // devDebug(`ignoring path "${key}" from state cleanup`);
    return;
  }

  // This handles the case of manual deletion from writeState
  if (isDelete) {
    keyPaths.delete(key);
    return;
  }

  emitter.emit(EVENT_ADD_KEY, key);

  if (keyPaths.has(key)) {
    keyPaths.delete(key);
    keyPaths.add(key);
    return;
  }

  keyPaths.add(key);

  if (keyPaths.size > CONFIG.MAX_LENGTH) {
    const keys: string[] = [...keyPaths] as string[];
    const firstKey: string = keys[0];

    keyPaths.delete(firstKey);

    const [curr, lastKey] = digPath(
      firstKey.split(PATH_DELIMITER),
      CONFIG.STATE,
    );

    // This is potentially unsafe.
    if (curr) {
      delete curr[lastKey];
      devDebug(`Cleaned up stale key path at ${firstKey}`);
    }
  }
}
